import api from './api';

export interface TicketAttachment {
  id: string;
  filename: string;
  mimetype: string;
  size: number;
  createdAt: string;
}

export const attachmentService = {
  /**
   * Upload de anexo para ticket
   */
  async upload(ticketId: string, file: File, apontamentoId?: string): Promise<TicketAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (apontamentoId) {
      formData.append('apontamentoId', apontamentoId);
    }

    const response = await api.post(`/tickets/${ticketId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Lista anexos de um ticket
   */
  async list(ticketId: string, apontamentoId?: string): Promise<TicketAttachment[]> {
    const params = apontamentoId ? { apontamentoId } : {};
    const response = await api.get(`/tickets/${ticketId}/attachments`, { params });
    return response.data;
  },

  /**
   * Busca o blob do anexo (com nome do arquivo)
   */
  async getBlob(ticketId: string, attachmentId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(`/tickets/${ticketId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    });

    return { blob, filename };
  },

  /**
   * Download de anexo
   */
  async download(ticketId: string, attachmentId: string): Promise<void> {
    const { blob, filename } = await this.getBlob(ticketId, attachmentId);

    // Criar URL temporária e fazer download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Visualizar anexo (abre em nova aba para renderização inline)
   */
  async view(ticketId: string, attachmentId: string): Promise<void> {
    const { blob } = await this.getBlob(ticketId, attachmentId);
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },

  /**
   * Remove anexo
   */
  async delete(ticketId: string, attachmentId: string): Promise<void> {
    await api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
  },

  /**
   * Formatar tamanho do arquivo
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Obter ícone baseado no tipo de arquivo
   */
  getFileIcon(mimetype: string): string {
    if (mimetype.startsWith('image/')) return '🖼️';
    if (mimetype === 'application/pdf') return '📄';
    if (mimetype === 'text/plain') return '📝';
    if (mimetype === 'text/csv') return '📊';
    if (mimetype.includes('zip')) return '📦';
    return '📎';
  },
};